import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, Output, inject, input } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { AddressDoctorExportsModule } from 'src/app/extensions/address-doctor/exports/address-doctor-exports.module';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import { Address } from 'ish-core/models/address/address.model';
import { FeatureEventService } from 'ish-core/utils/feature-event/feature-event.service';
import { whenTruthy } from 'ish-core/utils/operators';
import { AddressComponent } from 'ish-shared/components/address/address/address.component';
import { FormlyCustomerAddressFormComponent } from 'ish-shared/formly-address-forms/components/formly-customer-address-form/formly-customer-address-form.component';
import { FormsService } from 'ish-shared/forms/utils/forms.service';

/**
 * Standalone widget component for selecting and setting the basket invoice address in the checkout.
 */
@Component({
  selector: 'ish-basket-invoice-address-widget',
  templateUrl: './basket-invoice-address-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    FaIconComponent,
    AddressComponent,
    ReactiveFormsModule,
    FormlyModule,
    NgbCollapse,
    FormlyCustomerAddressFormComponent,
    AddressDoctorExportsModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class BasketInvoiceAddressWidgetComponent implements OnInit, OnDestroy {
  private checkoutFacade = inject(CheckoutFacade);
  private accountFacade = inject(AccountFacade);
  private featureToggleService = inject(FeatureToggleService);
  private featureEventService = inject(FeatureEventService);

  readonly eligibleAddresses$ = input.required<Observable<Address[]>>();
  readonly showErrors = input(true);

  @Output() collapseChange = new BehaviorSubject(true);

  @Input()
  set collapse(value: boolean) {
    this.collapseChange.next(value);
    if (value) {
      this.editAddress = {};
    }
  }
  invoiceAddress$: Observable<Address>;
  addresses$: Observable<Address[]>;
  customerAddresses$: Observable<Address[]>;
  isLoggedIn$: Observable<boolean>;

  form = new UntypedFormGroup({});
  fields: FormlyFieldConfig[];
  editAddress: Partial<Address>;
  emptyOptionLabel = 'checkout.addresses.select_invoice_address.button';

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.customerAddresses$ = this.accountFacade.addresses$().pipe(shareReplay(1));
    this.invoiceAddress$ = this.checkoutFacade.basketInvoiceAddress$;

    this.invoiceAddress$
      .pipe(
        map(address =>
          address
            ? 'checkout.addresses.select_a_different_invoice_address.default'
            : 'checkout.create_invoice_address.link'
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(label => (this.emptyOptionLabel = label));

    // prepare data for invoice select drop down
    this.addresses$ = combineLatest([this.customerAddresses$, this.invoiceAddress$]).pipe(
      map(([addresses, invoiceAddress]) =>
        addresses?.filter(address => address.invoiceToAddress).filter(address => address.id !== invoiceAddress?.id)
      )
    );

    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;

    this.fields = [
      {
        key: 'id',
        type: 'custom-select-field',
        props: {
          fieldClass: 'col-12',
          options: FormsService.getAddressOptions(this.addresses$),
          placeholder: this.emptyOptionLabel,
        },
        hooks: {
          onInit: field => {
            field.form
              .get('id')
              .valueChanges.pipe(whenTruthy(), takeUntil(this.destroy$))
              .subscribe(addressId => this.checkoutFacade.assignBasketAddress(addressId, 'invoice'));
          },
        },
      },
    ];

    // preassign an invoice address if the user has only one invoice address
    combineLatest([this.addresses$, this.checkoutFacade.basket$])
      .pipe(
        // prevent assigning the address at an anonymous basket after login
        filter(([addresses, basket]) => !!basket?.customerNo && !!addresses?.length),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(([addresses, basket]) => {
        if (!basket.invoiceToAddress && addresses.length === 1) {
          this.checkoutFacade.assignBasketAddress(addresses[0].id, 'invoice');
        }
      });

    this.invoiceAddress$.pipe(takeUntil(this.destroy$)).subscribe(() => (this.collapse = true));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showAddressForm(address?: Address) {
    if (address) {
      this.editAddress = { ...address };
    } else {
      this.editAddress = {};
    }
    this.collapse = false;
  }

  saveAddress(address: Address) {
    if (this.editAddress && Object.keys(this.editAddress).length > 0) {
      if (this.featureToggleService.enabled('addressDoctor')) {
        const id = this.featureEventService.sendNotification('addressDoctor', 'check-address', {
          address,
        });

        this.featureEventService
          .eventResultListener$('addressDoctor', 'check-address', id)
          .pipe(whenTruthy(), take(1), takeUntil(this.destroy$))
          .subscribe(({ data }) => {
            if (data) {
              this.checkoutFacade.customUpdateBasketAddress(data, 'invoice');
              this.collapse = true;
            }
          });
      } else {
        this.checkoutFacade.customUpdateBasketAddress(address, 'invoice');
        this.collapse = true;
      }
    } else {
      if (this.featureToggleService.enabled('addressDoctor')) {
        const id = this.featureEventService.sendNotification('addressDoctor', 'check-address', {
          address,
        });

        this.featureEventService
          .eventResultListener$('addressDoctor', 'check-address', id)
          .pipe(whenTruthy(), take(1), takeUntil(this.destroy$))
          .subscribe(({ data }) => {
            if (data) {
              this.checkoutFacade.createBasketAddress(data, 'invoice');
            }
          });
      } else {
        this.checkoutFacade.createBasketAddress(address, 'invoice');
      }
      (this.form.get('id') as UntypedFormControl).setValue('', { emitEvent: false });
    }
  }

  cancelEditAddress() {
    this.collapse = true;
  }
}
